// Organizasyon teklifleri listesi (/events) — nişan, toplantı, mezuniyet gecesi …
//
// Veri: eventApi.js (tüm uçlar token ister). Sunucu GET biçimi: tutarlar number, tarihler 'YYYY-MM-DD' string,
// items dizi, depositPaid boolean, createdBy/updatedBy {uid,email}. Liste yalnızca bu ekranda tutulur (DataContext'e girmez).
// Süzme sırası: üstteki sezon seçicisi → arama / tür / etkinlik tarihi → durum.
// Durum kartları durum DIŞINDAKİ süzgeçlerin sonucunu sayar; karta tıklamak durum süzgecini seçer.
// Tutarlar para birimine göre ayrı toplanır (döviz TL'ye çevrilmez).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import {
  Plus, FileText, Trash2, Search, Eye, X, Pencil, MessageCircle, Mail, AlertTriangle, Copy, Clock,
  CheckCircle, XCircle, RefreshCw, Link2, CalendarDays, Users, MapPin, Wallet, Hourglass, Lock, FilterX, PartyPopper,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSeason } from '../../context/SeasonContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { API_URL } from '../../utils/api';
import { formatMoneyTR, discountLabel, round2 } from '../../utils/pricing';
import {
  eventOfferTotals, eventLineTotal, isEventOfferExpired, todayLocal, EVENT_STATUSES, itemVatRate, visibleVatGroups, vatRatesText,
  hasZeroVatItems,
} from '../../utils/eventPricing';
import { offerInSeason, offerSeason, ALL_SEASONS } from '../../utils/season';
import {
  fetchEventOffers, fetchEventOffer, setEventOfferStatus, setEventDepositPaid, deleteEventOffer,
  uploadEventPdf, eventPdfLink, fetchEventSettings, VAT_MODE_LABELS,
} from './eventApi';
import { EventPDF } from './EventPDF';

const PAGE_SIZE = 30;
// PDF oluşturucu görsellere tam URL ister ('/uploads/…' → sunucu adresi). API_URL kopyalanmaz, ondan türetilir.
const SERVER_URL = API_URL.replace(/\/api$/, '');
const LOGO_KEYS = ['light', 'dark', 'favicon', 'pdfBackground', 'loginImage'];

const EMAIL_SUBJECT = 'Organizasyon Teklifi - Ramada by Wyndham Ergan';
const CONTACT_LINES = ['444 1 344', 'info@ramadaergan.com', 'www.ramadaergan.com'];
const NO_PDF_HINT = 'PDF oluşturulmamış ya da teklif sonradan değişmiş — paylaşmak için önce "PDF\'i yeniden oluştur"a basın.';

const STATUS_PILL = {
  'Onay Bekliyor': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  'Onaylandı': 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  'Onaylanmadı': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};
const STATUS_ROW = {
  'Onay Bekliyor': 'bg-yellow-50/60 dark:bg-yellow-900/10',
  'Onaylandı': 'bg-green-50/60 dark:bg-green-900/10',
  'Onaylanmadı': 'bg-red-50/60 dark:bg-red-900/10',
};
const pillCls = (status) => STATUS_PILL[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';

const STATUS_CARDS = [
  { status: '', label: 'Toplam', Icon: FileText, text: 'text-blue-600 dark:text-blue-400', iconBg: 'bg-blue-50 dark:bg-blue-900/20', active: 'border-blue-500 ring-1 ring-blue-500 dark:border-blue-400 dark:ring-blue-400' },
  { status: 'Onay Bekliyor', label: 'Onay Bekliyor', Icon: Clock, text: 'text-yellow-600 dark:text-yellow-400', iconBg: 'bg-yellow-50 dark:bg-yellow-900/20', active: 'border-yellow-500 ring-1 ring-yellow-500 dark:border-yellow-400 dark:ring-yellow-400' },
  { status: 'Onaylandı', label: 'Onaylandı', Icon: CheckCircle, text: 'text-green-600 dark:text-green-400', iconBg: 'bg-green-50 dark:bg-green-900/20', active: 'border-green-500 ring-1 ring-green-500 dark:border-green-400 dark:ring-green-400' },
  { status: 'Onaylanmadı', label: 'Onaylanmadı', Icon: XCircle, text: 'text-red-600 dark:text-red-400', iconBg: 'bg-red-50 dark:bg-red-900/20', active: 'border-red-500 ring-1 ring-red-500 dark:border-red-400 dark:ring-red-400' },
];

const TONES = {
  gray: {
    icon: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700',
    button: 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600',
  },
  blue: {
    icon: 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30',
    button: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40',
  },
  teal: {
    icon: 'text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30',
    button: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40',
  },
  green: {
    icon: 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30',
    button: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40',
  },
  purple: {
    icon: 'text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30',
    button: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40',
  },
  orange: {
    icon: 'text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/30',
    button: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40',
  },
  red: {
    icon: 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30',
    button: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40',
  },
};

const inputCls = 'w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400';
const labelCls = 'block text-xs text-gray-500 dark:text-gray-400 mb-1 ml-1';
const thCls = 'px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider';

// --- Biçim yardımcıları ---------------------------------------------------------------------------

// 'YYYY-MM-DD' → yerel Date (UTC kayması olmasın diye new Date(y, m-1, d))
const parseLocalDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const LONG_DATE = { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' };
const SHORT_DATE = { day: '2-digit', month: 'short', year: 'numeric' };
const fmtDate = (value, opts = { day: 'numeric', month: 'long', year: 'numeric' }) => {
  const d = parseLocalDate(value);
  return d ? d.toLocaleDateString('tr-TR', opts) : '';
};
const fmtWeekday = (value) => {
  const d = parseLocalDate(value);
  return d ? d.toLocaleDateString('tr-TR', { weekday: 'long' }) : '';
};
// Sunucu DATETIME'ı ISO metin olarak döner (createdAt, updatedAt, depositPaidAt)
const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDay = (value) => toDate(value)?.toLocaleDateString('tr-TR') || '';
const fmtDateTime = (value) => {
  const d = toDate(value);
  return d ? `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : '';
};
const timeRange = (o) => (o.startTime && o.endTime ? `${o.startTime}–${o.endTime}` : (o.startTime || (o.endTime ? `– ${o.endTime}` : '')));
const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
const money = (n, currency) => `${formatMoneyTR(n)} ${currency || 'TL'}`;
const offerCurrency = (o) => o?.currency || 'TL';

// Yaklaşan etkinlik: "Bugün" / "Yarın" / "5 gün kaldı" (iki hafta içi)
const daysLeftLabel = (eventDate, today) => {
  const d = parseLocalDate(eventDate);
  const t = parseLocalDate(today);
  if (!d || !t) return '';
  const diff = Math.round((d - t) / 86400000);
  if (diff === 0) return 'Bugün';
  if (diff === 1) return 'Yarın';
  if (diff > 1 && diff <= 14) return `${diff} gün kaldı`;
  return '';
};

// Para birimine göre toplam → [['TL', 125000], ['EUR', 2000]] (TL önce)
const sumByCurrency = (list) => {
  const map = new Map();
  list.forEach(o => {
    const c = offerCurrency(o);
    map.set(c, round2((map.get(c) || 0) + (Number(o.totalPrice) || 0)));
  });
  return [...map.entries()].sort(([a], [b]) => (a === 'TL' ? -1 : b === 'TL' ? 1 : a.localeCompare(b)));
};

// Telefon → wa.me biçimi (OfferList ile aynı TR varsayımı; '00' ile başlayan uluslararası numara da desteklenir)
const waPhone = (raw) => {
  let phone = String(raw || '').replace(/\D/g, '');
  if (phone.startsWith('00')) phone = phone.slice(2);
  else if (phone.startsWith('0')) phone = `9${phone}`;
  else if (phone.length === 10) phone = `90${phone}`;
  return phone;
};

// "Nişan organizasyonunuz" — tür yoksa ya da "Diğer" ise yalnızca "organizasyonunuz"
const typePhrase = (o, capitalize = false) => {
  const type = String(o.eventType || '').trim();
  if (type && type !== 'Diğer') return `${type} organizasyonunuz`;
  return capitalize ? 'Organizasyonunuz' : 'organizasyonunuz';
};

const offerLabel = (o) => [o.customerName, o.companyName, o.eventType].filter(Boolean).join(' · ');

const searchText = (o) => [o.customerName, o.companyName, o.eventName, o.eventType, o.phone, o.email]
  .filter(Boolean).join(' ').toLocaleLowerCase('tr');
const digitsOnly = (s) => String(s || '').replace(/\D/g, '');

// Sıralama
const createdMs = (o) => {
  const t = Date.parse(o?.createdAt || '');
  return Number.isFinite(t) ? t : 0;
};
const byCreatedDesc = (a, b) => (createdMs(b) - createdMs(a)) || ((Number(b.id) || 0) - (Number(a.id) || 0));
// En yakın etkinlik: önce yaklaşanlar (tarihe göre artan), sonra geçmişler (en yeniden), en sonda tarihsizler
const byNearestEvent = (today) => (a, b) => {
  const group = (o) => (!o.eventDate ? 2 : (String(o.eventDate) >= today ? 0 : 1));
  const ga = group(a);
  const gb = group(b);
  if (ga !== gb) return ga - gb;
  if (ga === 0) {
    return String(a.eventDate).localeCompare(String(b.eventDate))
      || String(a.startTime || '').localeCompare(String(b.startTime || ''))
      || byCreatedDesc(a, b);
  }
  if (ga === 1) return String(b.eventDate).localeCompare(String(a.eventDate)) || byCreatedDesc(a, b);
  return byCreatedDesc(a, b);
};

// --- Küçük sunum bileşenleri -----------------------------------------------------------------------

function ActionButton({ action, compact }) {
  const { Icon, label, title, onClick, disabled, tone, spinning } = action;
  const tc = TONES[tone] || TONES.gray;
  const icon = <Icon size={compact ? 18 : 16} className={spinning ? 'animate-spin' : undefined} />;
  if (compact) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} title={title || label} aria-label={label}
        className={`p-1.5 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent ${tc.icon}`}>
        {icon}
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title || label}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tc.button}`}>
      {icon}
      {label}
    </button>
  );
}

function Field({ label, children }) {
  if (children === null || children === undefined || children === '' || children === false) return null;
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-32 shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="min-w-0 break-words text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{children}</h4>;
}

function TotalLine({ label, value, className = '' }) {
  return (
    <div className={`flex justify-between gap-4 text-sm ${className}`}>
      <span>{label}</span>
      <span className="tabular-nums whitespace-nowrap">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------

export default function EventList() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedSeason } = useSeason();
  const token = currentUser?.token;
  const canWrite = !!currentUser?.permissions?.write;
  const canDelete = !!currentUser?.permissions?.delete;
  const canSeeMoney = !!currentUser?.permissions?.view_financials;

  const [offers, setOffers] = useState(null);   // null = henüz yüklenmedi
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState({});         // { [id]: 'status' | 'deposit' | 'pdf' | 'delete' }
  const [notice, setNotice] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eventFrom, setEventFrom] = useState('');
  const [eventTo, setEventTo] = useState('');
  const [sortBy, setSortBy] = useState('created'); // 'created' | 'event'
  const [currentPage, setCurrentPage] = useState(1);

  // PDF ayarları (logolar + banka + organizasyon ayarları) ilk "yeniden oluştur"da yüklenir, sayfa boyunca saklanır
  const pdfSettingsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchEventOffers(token);
      setOffers(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Organizasyon teklifleri yüklenemedi:', e);
      setError(e.message || 'Organizasyon teklifleri alınamadı.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Filtre / sıralama / sezon değişince ilk sayfa
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter, statusFilter, eventFrom, eventTo, sortBy, selectedSeason]);

  // Bilgi mesajı birkaç saniye sonra kaybolur
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  // Detay penceresi Esc ile kapanır
  useEffect(() => {
    if (selectedId === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  const patchOffer = (id, patch) =>
    setOffers(prev => (prev ? prev.map(o => (o.id === id ? { ...o, ...patch } : o)) : prev));
  const markBusy = (id, what) => setBusy(prev => {
    const next = { ...prev };
    if (what) next[id] = what; else delete next[id];
    return next;
  });

  // --- Yazma işlemleri (sunucu işlem logunu kendisi yazar) ---

  const handleStatusChange = async (offer, status) => {
    if (!status || status === offer.status) return;
    const prev = offer.status;
    markBusy(offer.id, 'status');
    patchOffer(offer.id, { status }); // iyimser güncelleme
    try {
      await setEventOfferStatus(token, offer.id, status);
    } catch (e) {
      patchOffer(offer.id, { status: prev });
      alert(e.message || 'Durum güncellenemedi.');
    } finally {
      markBusy(offer.id, null);
    }
  };

  const toggleDeposit = async (offer) => {
    const next = !offer.depositPaid;
    const amount = canSeeMoney && offer.depositAmount ? ` (${money(offer.depositAmount, offerCurrency(offer))})` : '';
    const question = next
      ? `${offerLabel(offer)}\n\nKapora${amount} ALINDI olarak işaretlensin mi?`
      : `${offerLabel(offer)}\n\nKapora ALINMADI olarak işaretlensin mi?`;
    if (!window.confirm(question)) return;

    const prev = { depositPaid: offer.depositPaid, depositPaidAt: offer.depositPaidAt };
    markBusy(offer.id, 'deposit');
    patchOffer(offer.id, { depositPaid: next, depositPaidAt: next ? new Date().toISOString() : null });
    let ok = false;
    try {
      await setEventDepositPaid(token, offer.id, next);
      ok = true;
    } catch (e) {
      patchOffer(offer.id, prev);
      alert(e.message || 'Kapora bilgisi güncellenemedi.');
    } finally {
      markBusy(offer.id, null);
    }
    // Kapora alındıysa teklif genelde onaylanmıştır — sor, kendiliğinden değiştirme
    if (ok && next && offer.status === 'Onay Bekliyor'
      && window.confirm('Kapora alındı. Teklifin durumu da "Onaylandı" yapılsın mı?')) {
      await handleStatusChange({ ...offer, depositPaid: true }, 'Onaylandı');
    }
  };

  const handleDelete = async (offer) => {
    const ok = window.confirm(
      `"${offerLabel(offer)}" organizasyon teklifi silinsin mi?\n\n`
      + 'Bu işlem geri alınamaz; müşteriye gönderilen PDF linki de çalışmaz hale gelir.',
    );
    if (!ok) return;
    markBusy(offer.id, 'delete');
    try {
      await deleteEventOffer(token, offer.id);
      setOffers(prev => (prev || []).filter(o => o.id !== offer.id));
      setSelectedId(cur => (cur === offer.id ? null : cur));
      setNotice('Organizasyon teklifi silindi.');
    } catch (e) {
      alert(e.message || 'Organizasyon teklifi silinemedi.');
    } finally {
      markBusy(offer.id, null);
    }
  };

  const loadPdfSettings = async () => {
    if (pdfSettingsRef.current) return pdfSettingsRef.current;
    const [logosRes, bankRes, eventSettings] = await Promise.all([
      fetch(`${API_URL}/settings/logos`),
      fetch(`${API_URL}/settings/bank`),
      fetchEventSettings(),
    ]);
    if (!logosRes.ok || !bankRes.ok) throw new Error('Logo / banka ayarları alınamadı.');
    const logos = (await logosRes.json()) || {};
    const bank = (await bankRes.json()) || {};
    LOGO_KEYS.forEach(key => {
      if (typeof logos[key] === 'string' && logos[key].startsWith('/uploads')) logos[key] = SERVER_URL + logos[key];
    });
    pdfSettingsRef.current = { settings: { ...logos, bank }, eventSettings };
    return pdfSettingsRef.current;
  };

  // Kayıtlı teklifin PDF'ini (sunucudaki SON hali ile) yeniden üretir. Dosya adı değişmez → paylaşılan link aynı kalır.
  const regeneratePdf = async (offer) => {
    markBusy(offer.id, 'pdf');
    try {
      const [{ settings, eventSettings }, fresh] = await Promise.all([loadPdfSettings(), fetchEventOffer(token, offer.id)]);
      const render = (o) => pdf(<EventPDF offer={o} settings={settings} eventSettings={eventSettings} />).toBlob();
      let saved = fresh;
      let result = await uploadEventPdf(token, fresh.id, await render(fresh));
      // Dosya adı olmayan (eski/elle eklenmiş) kayıtta ad ilk yüklemede üretilir; PDF'teki link/QR için bir kez daha bas
      if (!fresh.pdfFile && result?.pdfFile) {
        saved = { ...fresh, pdfFile: result.pdfFile };
        result = await uploadEventPdf(token, fresh.id, await render(saved));
      }
      const pdfFile = result?.pdfFile || saved.pdfFile;
      const pdfGeneratedAt = result?.pdfGeneratedAt || new Date().toISOString();
      setOffers(prev => (prev ? prev.map(o => (o.id === offer.id ? { ...saved, pdfFile, pdfGeneratedAt } : o)) : prev));
      setNotice('PDF yeniden oluşturuldu. Müşteriye gönderilen link aynı kaldı.');
    } catch (e) {
      console.error('PDF oluşturma hatası:', e);
      alert(`PDF oluşturulamadı: ${e.message || 'bilinmeyen hata'}`);
    } finally {
      markBusy(offer.id, null);
    }
  };

  // --- Paylaşım ---

  const totalText = (o) => {
    const vat = visibleVatGroups(eventOfferTotals(o)).length > 0 ? ' (KDV dahil)' : '';
    return `${money(o.totalPrice, offerCurrency(o))}${vat}`;
  };

  const shareWhatsApp = (offer) => {
    const link = eventPdfLink(offer);
    if (!link) return;
    if (!offer.phone) {
      alert('Bu teklifte telefon numarası kayıtlı değil.');
      return;
    }
    // Tutar yalnızca finansal görme izni olan personelin mesajına eklenir
    const lines = [
      `Sayın ${offer.customerName}, ${typePhrase(offer)} için hazırladığımız teklif:`,
      link,
      '',
      ...(canSeeMoney ? [`Toplam: ${totalText(offer)}`] : []),
      ...(offer.validUntil ? [`Teklif geçerlilik tarihi: ${fmtDate(offer.validUntil)}`] : []),
      ...(canSeeMoney || offer.validUntil ? [''] : []),
      'Detaylı bilgi ve onay için bizimle iletişime geçebilirsiniz.',
      ...CONTACT_LINES,
    ];
    window.open(`https://wa.me/${waPhone(offer.phone)}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  const shareEmail = (offer) => {
    const link = eventPdfLink(offer);
    if (!link) return;
    if (!offer.email) {
      alert('Bu teklifte e-posta adresi kayıtlı değil.');
      return;
    }
    const lines = [
      `Sayın ${offer.customerName},`,
      '',
      `${typePhrase(offer, true)} için hazırladığımız teklifi aşağıdaki linkten görüntüleyebilirsiniz:`,
      '',
      link,
      '',
      ...(canSeeMoney ? [`Toplam: ${totalText(offer)}`] : []),
      ...(offer.validUntil ? [`Teklif geçerlilik tarihi: ${fmtDate(offer.validUntil)}`] : []),
      ...(canSeeMoney || offer.validUntil ? [''] : []),
      'Detaylı bilgi ve onay için bizimle iletişime geçebilirsiniz.',
      '',
      'Saygılarımızla,',
      'Ramada by Wyndham Ergan',
      CONTACT_LINES.join(' · '),
    ];
    window.location.href = `mailto:${offer.email}?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(lines.join('\n'))}`;
  };

  const copyLink = async (offer) => {
    const link = eventPdfLink(offer);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setNotice('Teklif linki panoya kopyalandı.');
    } catch {
      // Pano erişimi yoksa (güvensiz bağlam / izin) kullanıcı elle kopyalasın
      window.prompt('Linki kopyalayın (Ctrl+C):', link);
    }
  };

  // Satır işlemleri — tablo (simge), mobil kart ve detay penceresi (etiketli) aynı listeyi kullanır
  const actionsFor = (offer, { inModal = false } = {}) => {
    const link = eventPdfLink(offer);
    const noPdf = !link;
    const rowBusy = busy[offer.id];
    return [
      !inModal && { key: 'detail', label: 'Detay', Icon: Eye, tone: 'gray', onClick: () => setSelectedId(offer.id) },
      { key: 'pdf', label: 'PDF Aç', Icon: FileText, tone: 'blue', disabled: noPdf,
        title: noPdf ? NO_PDF_HINT : 'PDF\'i yeni sekmede aç', onClick: () => window.open(link, '_blank', 'noopener') },
      canWrite && { key: 'regen', label: rowBusy === 'pdf' ? 'PDF oluşturuluyor…' : 'PDF\'i yeniden oluştur', Icon: RefreshCw,
        tone: 'teal', disabled: !!rowBusy, spinning: rowBusy === 'pdf',
        title: 'Kayıtlı bilgilerle PDF\'i yeniden oluştur (link değişmez)', onClick: () => regeneratePdf(offer) },
      { key: 'wa', label: 'WhatsApp', Icon: MessageCircle, tone: 'green', disabled: noPdf,
        title: noPdf ? NO_PDF_HINT : 'WhatsApp ile gönder', onClick: () => shareWhatsApp(offer) },
      { key: 'mail', label: 'E-posta', Icon: Mail, tone: 'purple', disabled: noPdf,
        title: noPdf ? NO_PDF_HINT : 'E-posta ile gönder', onClick: () => shareEmail(offer) },
      { key: 'link', label: 'Linki kopyala', Icon: Link2, tone: 'gray', disabled: noPdf,
        title: noPdf ? NO_PDF_HINT : 'Teklif linkini kopyala', onClick: () => copyLink(offer) },
      canWrite && { key: 'edit', label: 'Düzenle', Icon: Pencil, tone: 'blue', onClick: () => navigate(`/events/edit/${offer.id}`) },
      canWrite && { key: 'copy', label: 'Kopyala', Icon: Copy, tone: 'orange', title: 'Bu tekliften yeni teklif oluştur',
        onClick: () => navigate(`/events/new?copy=${offer.id}`) },
      canDelete && { key: 'delete', label: 'Sil', Icon: Trash2, tone: 'red', disabled: !!rowBusy, onClick: () => handleDelete(offer) },
    ].filter(Boolean);
  };

  // --- Görünüm parçaları ---

  const renderStatus = (offer, wide = false) => (canWrite ? (
    <select
      value={offer.status}
      onChange={(e) => handleStatusChange(offer, e.target.value)}
      disabled={!!busy[offer.id]}
      aria-label="Teklif durumu"
      className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-wait ${pillCls(offer.status)} ${wide ? 'w-full sm:w-auto' : ''}`}
    >
      {EVENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      {!EVENT_STATUSES.includes(offer.status) && <option value={offer.status}>{offer.status}</option>}
    </select>
  ) : (
    <span className={`inline-block text-xs font-semibold rounded-full px-2 py-1 ${pillCls(offer.status)}`}>{offer.status}</span>
  ));

  const depositTooltip = (o) => {
    const parts = [];
    if (canSeeMoney && o.depositAmount) parts.push(`Kapora: ${money(o.depositAmount, offerCurrency(o))}`);
    if (o.depositPaid) parts.push(o.depositPaidAt ? `Alındı: ${fmtDateTime(o.depositPaidAt)}` : 'Alındı');
    else if (o.depositDueDate) parts.push(`Son ödeme: ${fmtDate(o.depositDueDate)}`);
    return parts.join(' · ');
  };

  const renderBadges = (o) => {
    const badges = [];
    if (isEventOfferExpired(o)) {
      badges.push(
        <span key="expired" title={`Geçerlilik tarihi: ${fmtDate(o.validUntil)}`}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
          <Hourglass size={12} /> Süresi doldu
        </span>,
      );
    }
    if (o.depositType) {
      const paid = !!o.depositPaid;
      const cls = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${paid
        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`;
      const content = <><Wallet size={12} /> {paid ? 'Kapora alındı' : 'Kapora bekleniyor'}</>;
      const tip = depositTooltip(o);
      badges.push(canWrite ? (
        <button key="deposit" type="button" onClick={() => toggleDeposit(o)} disabled={!!busy[o.id]}
          title={`${tip ? `${tip} — ` : ''}değiştirmek için tıklayın`}
          className={`${cls} hover:ring-1 hover:ring-current disabled:opacity-60 disabled:cursor-wait`}>
          {content}
        </button>
      ) : (
        <span key="deposit" className={cls} title={tip || undefined}>{content}</span>
      ));
    }
    return badges;
  };

  // --- Süzme ---

  const today = todayLocal();
  const all = offers || [];
  const seasonOffers = all.filter(o => offerInSeason(o, selectedSeason));

  const term = searchTerm.trim().toLocaleLowerCase('tr');
  const termDigits = digitsOnly(term).replace(/^0+/, '');
  const baseFiltered = seasonOffers.filter(o => {
    if (term) {
      const textHit = searchText(o).includes(term);
      const phoneHit = termDigits.length >= 3 && digitsOnly(o.phone).includes(termDigits);
      if (!textHit && !phoneHit) return false;
    }
    if (typeFilter && (o.eventType || '') !== typeFilter) return false;
    if (eventFrom || eventTo) {
      if (!o.eventDate) return false;
      if (eventFrom && o.eventDate < eventFrom) return false;
      if (eventTo && o.eventDate > eventTo) return false;
    }
    return true;
  });
  const filtered = baseFiltered
    .filter(o => (statusFilter ? o.status === statusFilter : true))
    .sort(sortBy === 'event' ? byNearestEvent(today) : byCreatedDesc);

  const typeOptions = [...new Set([...seasonOffers.map(o => o.eventType).filter(Boolean), ...(typeFilter ? [typeFilter] : [])])]
    .sort((a, b) => a.localeCompare(b, 'tr'));

  const filtersActive = !!(searchTerm || typeFilter || statusFilter || eventFrom || eventTo);
  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setStatusFilter('');
    setEventFrom('');
    setEventTo('');
  };

  // --- Sayfalama ---
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // 1 … 4 5 [6] 7 8 … 27
  const pageNumbers = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, safePage, safePage - 1, safePage + 1]);
    if (safePage <= 3) [2, 3, 4].forEach(p => pages.add(p));
    if (safePage >= totalPages - 2) [totalPages - 1, totalPages - 2, totalPages - 3].forEach(p => pages.add(p));
    const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    return sorted.reduce((acc, p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);
  })();

  const selected = selectedId === null ? null : all.find(o => o.id === selectedId) || null;

  // --- Ekran ---

  const header = (
    <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Organizasyon Teklifleri</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Nişan, toplantı, mezuniyet gecesi ve diğer etkinlik teklifleri</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={load} disabled={loading}
          className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-60">
          <RefreshCw size={18} className={loading ? 'animate-spin' : undefined} />
          Yenile
        </button>
        {canWrite && (
          <Link to="/events/new" className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors">
            <Plus size={20} />
            Yeni Organizasyon Teklifi
          </Link>
        )}
      </div>
    </div>
  );

  const errorBanner = error && (
    <div role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
      <span className="flex items-center gap-2"><AlertTriangle size={18} className="shrink-0" /> {error}</span>
      <button type="button" onClick={load} disabled={loading} className="font-medium underline disabled:opacity-60">Tekrar dene</button>
    </div>
  );

  if (offers === null) {
    return (
      <div>
        {header}
        {errorBanner || <LoadingSpinner />}
      </div>
    );
  }

  const emptyBox = (() => {
    if (all.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-10 text-center">
          <PartyPopper size={40} className="mx-auto mb-3 text-blue-500 dark:text-blue-400" />
          <p className="text-gray-700 dark:text-gray-200 font-medium mb-4">Henüz organizasyon teklifi yok.</p>
          {canWrite && (
            <Link to="/events/new" className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Plus size={18} /> Yeni Organizasyon Teklifi
            </Link>
          )}
        </div>
      );
    }
    if (seasonOffers.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
          {selectedSeason !== ALL_SEASONS
            ? `${selectedSeason} sezonunda organizasyon teklifi yok. Üstteki seçiciden başka bir sezon seçebilirsiniz.`
            : 'Organizasyon teklifi bulunamadı.'}
        </div>
      );
    }
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
        <p>Aranan kriterlere uygun organizasyon teklifi bulunamadı.</p>
        {filtersActive && (
          <button type="button" onClick={clearFilters} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
            <FilterX size={16} /> Filtreleri temizle
          </button>
        )}
      </div>
    );
  })();

  const selTotals = selected ? eventOfferTotals(selected) : null;
  const selVatGroups = visibleVatGroups(selTotals);
  const selCur = selected ? offerCurrency(selected) : 'TL';
  const selItems = Array.isArray(selected?.items) ? selected.items : [];
  const selHasPlan = !!(selected && (selected.depositType || selected.depositDueDate || selected.balanceDueDate));

  return (
    <div>
      {header}
      {errorBanner}

      {/* Durum kartları — durum dışındaki süzgeçlerin sonucunu sayar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {STATUS_CARDS.map(card => {
          const list = card.status ? baseFiltered.filter(o => o.status === card.status) : baseFiltered;
          const sums = canSeeMoney ? sumByCurrency(list) : [];
          const active = statusFilter === card.status;
          return (
            <button
              key={card.label}
              type="button"
              onClick={() => setStatusFilter(card.status)}
              aria-pressed={active}
              className={`text-left bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border ${active ? card.active : 'border-gray-100 dark:border-gray-700'} flex items-start justify-between gap-2 hover:shadow-md transition-all`}
            >
              <div className="min-w-0">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1 font-medium">{card.label}</p>
                <p className={`text-2xl font-bold ${card.text}`}>{list.length}</p>
                {canSeeMoney && (
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 space-y-0.5"
                    title="Para birimleri ayrı toplanır; döviz tutarları TL'ye çevrilmez.">
                    {sums.length === 0
                      ? <span className="text-gray-400">0 TL</span>
                      : sums.map(([cur, total]) => <div key={cur} className="font-semibold tabular-nums">{money(total, cur)}</div>)}
                  </div>
                )}
              </div>
              <div className={`p-3 rounded-full shrink-0 ${card.iconBg} ${card.text}`}>
                <card.Icon size={22} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-3 items-end">
          <div className="xl:col-span-4">
            <label htmlFor="event-search" className={labelCls}>Ara</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
              <input
                id="event-search"
                type="search"
                placeholder="Müşteri, firma, etkinlik adı ya da telefon…"
                className={`${inputCls} pl-10`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="xl:col-span-2">
            <label htmlFor="event-type" className={labelCls}>Organizasyon türü</label>
            <select id="event-type" className={inputCls} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Tüm türler</option>
              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="xl:col-span-3">
            <span className={labelCls}>Etkinlik tarihi</span>
            <div className="flex gap-2">
              <input type="date" aria-label="Etkinlik tarihi başlangıç" className={inputCls} value={eventFrom}
                max={eventTo || undefined} onChange={(e) => setEventFrom(e.target.value)} />
              <input type="date" aria-label="Etkinlik tarihi bitiş" className={inputCls} value={eventTo}
                min={eventFrom || undefined} onChange={(e) => setEventTo(e.target.value)} />
            </div>
          </div>
          <div className="xl:col-span-2">
            <label htmlFor="event-sort" className={labelCls}>Sıralama</label>
            <select id="event-sort" className={inputCls} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="created">En yeni kayıt</option>
              <option value="event">En yakın etkinlik tarihi</option>
            </select>
          </div>
          <div className="xl:col-span-1">
            {filtersActive && (
              <button type="button" onClick={clearFilters} title="Filtreleri temizle"
                className="w-full p-2 rounded-lg flex items-center justify-center gap-1 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                <FilterX size={16} /> Temizle
              </button>
            )}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? emptyBox : (
        <>
          {/* Mobil: kartlar */}
          <div className="md:hidden space-y-4">
            {pageItems.map(o => {
              const past = !!o.eventDate && o.eventDate < today;
              const soon = daysLeftLabel(o.eventDate, today);
              const badges = renderBadges(o);
              const noPdf = !eventPdfLink(o);
              return (
                <div key={o.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-100 dark:border-gray-700 p-4 ${past ? 'opacity-60' : ''}`}>
                  <div className="flex justify-between items-start gap-3">
                    <button type="button" onClick={() => setSelectedId(o.id)} className="text-left min-w-0">
                      <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 truncate">
                        {[o.eventType || 'Organizasyon', o.eventName].filter(Boolean).join(' · ')}
                      </p>
                      <h3 className="font-bold text-gray-800 dark:text-white truncate">{o.customerName}</h3>
                      {o.companyName && <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{o.companyName}</p>}
                    </button>
                    {canSeeMoney && (
                      <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{money(o.totalPrice, offerCurrency(o))}</span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays size={14} />
                      {o.eventDate ? [fmtDate(o.eventDate, SHORT_DATE), timeRange(o)].filter(Boolean).join(' · ') : 'Tarih yok'}
                    </span>
                    {o.guestCount ? <span className="inline-flex items-center gap-1"><Users size={14} /> {fmtNumber(o.guestCount)} kişi</span> : null}
                    {o.venue && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {o.venue}</span>}
                    {soon && <span className="font-semibold text-blue-600 dark:text-blue-400">{soon}</span>}
                    {past && <span>Etkinlik geçti</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {renderStatus(o)}
                    {badges}
                  </div>
                  <details className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <summary className="cursor-pointer select-none text-sm font-medium text-gray-600 dark:text-gray-300">İşlemler</summary>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {actionsFor(o).map(a => <ActionButton key={a.key} action={a} />)}
                    </div>
                    {noPdf && <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">{NO_PDF_HINT}</p>}
                  </details>
                </div>
              );
            })}
          </div>

          {/* Masaüstü: tablo */}
          <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className={thCls}>Etkinlik Tarihi</th>
                  <th className={thCls}>Organizasyon</th>
                  <th className={thCls}>Müşteri</th>
                  <th className={`${thCls} text-right`}>Kişi</th>
                  <th className={thCls}>Salon</th>
                  {canSeeMoney && <th className={`${thCls} text-right`}>Genel Toplam</th>}
                  <th className={thCls}>Durum</th>
                  <th className={`${thCls} text-right`}>İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageItems.map(o => {
                  const past = !!o.eventDate && o.eventDate < today;
                  const soon = daysLeftLabel(o.eventDate, today);
                  const badges = renderBadges(o);
                  return (
                    <tr key={o.id}
                      className={`${STATUS_ROW[o.status] || ''} ${past ? 'opacity-60' : ''} hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors`}
                      title={past ? 'Etkinlik tarihi geçti' : undefined}>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {o.eventDate ? (
                          <>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{fmtDate(o.eventDate, SHORT_DATE)}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {[fmtWeekday(o.eventDate), timeRange(o)].filter(Boolean).join(' · ')}
                            </div>
                          </>
                        ) : <span className="text-sm text-gray-400">Tarih yok</span>}
                        {soon && <div className="text-xs font-semibold text-blue-600 dark:text-blue-400">{soon}</div>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{o.eventType || 'Organizasyon'}</div>
                        {o.eventName && <div className="text-sm text-gray-600 dark:text-gray-300">{o.eventName}</div>}
                        <div className="text-[11px] text-gray-400">#{o.id}{fmtDay(o.createdAt) ? ` · kayıt ${fmtDay(o.createdAt)}` : ''}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <button type="button" onClick={() => setSelectedId(o.id)}
                          className="text-left text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                          {o.customerName}
                        </button>
                        {o.companyName && <div className="text-sm text-gray-500 dark:text-gray-400">{o.companyName}</div>}
                        {o.phone && <div className="text-xs text-gray-400">{o.phone}</div>}
                      </td>
                      <td className="px-4 py-3 align-top text-right text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                        {o.guestCount ? fmtNumber(o.guestCount) : '-'}
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-700 dark:text-gray-200">{o.venue || '-'}</td>
                      {canSeeMoney && (
                        <td className="px-4 py-3 align-top text-right text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap tabular-nums">
                          {money(o.totalPrice, offerCurrency(o))}
                        </td>
                      )}
                      <td className="px-4 py-3 align-top">
                        {renderStatus(o)}
                        {badges.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{badges}</div>}
                      </td>
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-0.5">
                          {actionsFor(o).map(a => <ActionButton key={a.key} action={a} compact />)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-800 dark:text-gray-200">{filtered.length}</span> teklif içinden{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, filtered.length)}
              </span>{' '}
              arası gösteriliyor
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Önceki
                </button>
                {pageNumbers.map((page, i) => (page === '...' ? (
                  <span key={`gap-${i}`} className="px-2 text-gray-400 select-none">…</span>
                ) : (
                  <button key={page} type="button" onClick={() => goToPage(page)} aria-current={page === safePage ? 'page' : undefined}
                    className={`min-w-[36px] px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${page === safePage
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                    {page}
                  </button>
                )))}
                <button type="button" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Sonraki
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detay penceresi */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedId(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="event-detail-title" onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-start gap-4 p-5 border-b dark:border-gray-700">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                  {selected.eventType || 'Organizasyon'} · #{selected.id}
                </p>
                <h3 id="event-detail-title" className="text-xl font-bold text-gray-800 dark:text-white break-words">
                  {selected.eventName || selected.customerName}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {renderStatus(selected)}
                  {renderBadges(selected)}
                </div>
              </div>
              <button type="button" onClick={() => setSelectedId(null)} aria-label="Kapat"
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X size={24} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <section>
                  <SectionTitle>Müşteri ve Firma</SectionTitle>
                  <dl className="space-y-1.5">
                    <Field label="Ad soyad">{selected.customerName}</Field>
                    <Field label="Firma">{selected.companyName}</Field>
                    <Field label="Telefon">{selected.phone && <a href={`tel:${selected.phone}`} className="text-blue-600 dark:text-blue-400 hover:underline">{selected.phone}</a>}</Field>
                    <Field label="E-posta">{selected.email && <a href={`mailto:${selected.email}`} className="text-blue-600 dark:text-blue-400 hover:underline break-all">{selected.email}</a>}</Field>
                    <Field label="Vergi dairesi">{selected.taxOffice}</Field>
                    <Field label="Vergi no">{selected.taxNumber}</Field>
                    <Field label="Adres">{selected.address && <span className="whitespace-pre-line">{selected.address}</span>}</Field>
                  </dl>
                </section>
                <section>
                  <SectionTitle>Etkinlik</SectionTitle>
                  <dl className="space-y-1.5">
                    <Field label="Tür">{selected.eventType}</Field>
                    <Field label="Etkinlik adı">{selected.eventName}</Field>
                    <Field label="Tarih">{fmtDate(selected.eventDate, LONG_DATE)}</Field>
                    <Field label="Saat">{timeRange(selected)}</Field>
                    <Field label="Kişi sayısı">{selected.guestCount ? `${fmtNumber(selected.guestCount)} kişi` : ''}</Field>
                    <Field label="Salon / alan">{selected.venue}</Field>
                  </dl>
                </section>
              </div>

              <section>
                <SectionTitle>Kalemler</SectionTitle>
                {selItems.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Kalem yok.</p>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase text-gray-500 dark:text-gray-300">
                        <tr>
                          <th className="px-3 py-2 text-left w-8">#</th>
                          <th className="px-3 py-2 text-left">Kalem</th>
                          <th className="px-3 py-2 text-right">Miktar</th>
                          {canSeeMoney && <th className="px-3 py-2 text-right">Birim Fiyat</th>}
                          {canSeeMoney && <th className="px-3 py-2 text-right">KDV</th>}
                          {canSeeMoney && <th className="px-3 py-2 text-right">Tutar</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {selItems.map((it, i) => (
                          <tr key={i} className="align-top">
                            <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900 dark:text-white">{it.name}</div>
                              {it.description && <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line">{it.description}</div>}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap text-gray-700 dark:text-gray-200">
                              {fmtNumber(it.quantity)} {it.unit}
                            </td>
                            {canSeeMoney && <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-gray-700 dark:text-gray-200">{money(it.unitPrice, selCur)}</td>}
                            {canSeeMoney && <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-gray-500 dark:text-gray-400">%{fmtNumber(itemVatRate(it, selected.vatRate))}</td>}
                            {canSeeMoney && <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums font-medium text-gray-900 dark:text-white">{money(eventLineTotal(it), selCur)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {(canSeeMoney || selHasPlan) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {canSeeMoney && (
                    <section>
                      <SectionTitle>Tutarlar</SectionTitle>
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-1.5 text-gray-700 dark:text-gray-200">
                        <TotalLine label="Ara toplam" value={money(selTotals.subTotal, selCur)} />
                        {selTotals.discountAmount > 0 && (
                          <>
                            <TotalLine className="text-green-700 dark:text-green-400"
                              label={discountLabel({ name: selected.discountName || 'İndirim', type: selected.discountType, value: selected.discountValue })}
                              value={`-${money(selTotals.discountAmount, selCur)}`} />
                            <TotalLine label="İndirimli toplam" value={money(selTotals.afterDiscount, selCur)} />
                          </>
                        )}
                        {selVatGroups.map(g => (selected.vatMode === 'excluded' ? (
                          <TotalLine key={g.rate} label={`KDV (%${fmtNumber(g.rate)})`} value={`+${money(g.vat, selCur)}`} />
                        ) : (
                          <TotalLine key={g.rate} className="text-gray-500 dark:text-gray-400" label={`İçindeki KDV (%${fmtNumber(g.rate)})`}
                            value={money(g.vat, selCur)} />
                        )))}
                        <div className="pt-2 mt-1 border-t border-gray-200 dark:border-gray-700">
                          <TotalLine className="text-base font-bold text-gray-900 dark:text-white" label="Genel toplam" value={money(selTotals.total, selCur)} />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Fiyatlar {VAT_MODE_LABELS[selected.vatMode] || VAT_MODE_LABELS.included}
                            {selVatGroups.length > 0 ? ` (${vatRatesText(selVatGroups, fmtNumber)}${hasZeroVatItems(selTotals) ? "; %0 KDV'li kalemler de var" : ''})` : ''} girildi.
                          </p>
                        </div>
                      </div>
                    </section>
                  )}
                  {selHasPlan && (
                    <section>
                      <SectionTitle>Kapora / Ödeme Planı</SectionTitle>
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-1.5 text-gray-700 dark:text-gray-200">
                        {selected.depositType && canSeeMoney && (
                          <TotalLine
                            label={`Kapora${selected.depositType === 'percentage' ? ` (%${fmtNumber(selected.depositValue)})` : ''}`}
                            value={money(selTotals.depositAmount, selCur)} />
                        )}
                        {selected.depositDueDate && <TotalLine label="Kapora son ödeme" value={fmtDate(selected.depositDueDate)} />}
                        {selected.depositType && canSeeMoney && <TotalLine label="Kalan" value={money(selTotals.balance, selCur)} />}
                        {selected.balanceDueDate && <TotalLine label="Kalan son ödeme" value={fmtDate(selected.balanceDueDate)} />}
                        {selected.depositType && (
                          <div className="pt-2 mt-1 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2 text-sm">
                            <span className={selected.depositPaid ? 'text-green-700 dark:text-green-400 font-medium' : 'text-amber-700 dark:text-amber-400 font-medium'}>
                              {selected.depositPaid
                                ? `Kapora alındı${selected.depositPaidAt ? ` (${fmtDateTime(selected.depositPaidAt)})` : ''}`
                                : 'Kapora bekleniyor'}
                            </span>
                            {canWrite && (
                              <button type="button" onClick={() => toggleDeposit(selected)} disabled={!!busy[selected.id]}
                                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
                                {selected.depositPaid ? 'Alınmadı olarak işaretle' : 'Alındı olarak işaretle'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </section>
                  )}
                </div>
              )}

              {selected.notes && (
                <section>
                  <SectionTitle>Notlar</SectionTitle>
                  <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{selected.notes}</p>
                </section>
              )}

              {selected.internalNote && (
                <section className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                  <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-yellow-800 dark:text-yellow-300 mb-1">
                    <Lock size={14} /> İç not <span className="normal-case font-normal">(yalnızca panelde görünür, müşteriye gitmez)</span>
                  </h4>
                  <p className="text-sm text-yellow-900 dark:text-yellow-100 whitespace-pre-line">{selected.internalNote}</p>
                </section>
              )}

              <section>
                <SectionTitle>Diğer Bilgiler</SectionTitle>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Geçerlilik">
                    {selected.validUntil && (
                      <span className={isEventOfferExpired(selected) ? 'text-red-600 dark:text-red-400 font-medium' : undefined}>
                        {fmtDate(selected.validUntil)}{isEventOfferExpired(selected) ? ' (süresi doldu)' : ''}
                      </span>
                    )}
                  </Field>
                  <Field label="Sezon">{offerSeason(selected)}</Field>
                  <Field label="Kaynak">{selected.source?.name}</Field>
                  <Field label="Para birimi">{selCur}</Field>
                  <Field label="Oluşturan">
                    {[selected.createdBy?.email, fmtDateTime(selected.createdAt)].filter(Boolean).join(' · ')}
                  </Field>
                  <Field label="Son güncelleyen">
                    {selected.updatedBy?.email ? [selected.updatedBy.email, fmtDateTime(selected.updatedAt)].filter(Boolean).join(' · ') : ''}
                  </Field>
                </dl>
              </section>
            </div>

            <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg">
              {!eventPdfLink(selected) && <p className="mb-2 text-xs text-amber-700 dark:text-amber-400">{NO_PDF_HINT}</p>}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2">
                  {actionsFor(selected, { inModal: true }).map(a => <ActionButton key={a.key} action={a} />)}
                </div>
                <button type="button" onClick={() => setSelectedId(null)}
                  className="bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bilgi mesajı */}
      <div aria-live="polite" className="fixed bottom-4 right-4 z-[60]">
        {notice && (
          <div className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-3 text-sm text-white shadow-lg">
            <CheckCircle size={18} /> {notice}
          </div>
        )}
      </div>
    </div>
  );
}
